import { Chip, Spacer, type ChipProps } from "@heroui/react"

import type { RunMetricOutput } from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { getStat } from "../helpers/getStat"
import { parseResultJson } from "../helpers/parseResultJson"
import { WithAudioGateMixin } from "../mixins/WithAudioGateMixin"

interface SignalToNoiseResultJson {
  avg_audio_power?: number
  avg_silence_power?: number
  valid_audio_count?: number
  valid_silence_count?: number
}

interface SummaryStat {
  mean?: number
  number_of_data_points?: number
}

export class SignalToNoiseMetricRecord extends WithAudioGateMixin(
  NumericalMetricRecord,
) {
  overviewSize = "small" as const
  unit = "dB"
  aggregate_units = "dB"

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const value = mo.float_value || 0
    let qualityAssessment = ""
    let qualityColor: NonNullable<ChipProps["color"]> = "default"

    const resultData = parseResultJson<SignalToNoiseResultJson>(mo)
    const audioPowerStat = getStat<SummaryStat>(mo, "summary", "audio_power_db")
    const silencePowerStat = getStat<SummaryStat>(
      mo,
      "summary",
      "silence_power_db",
    )

    const avgAudioPower =
      audioPowerStat?.mean ?? resultData?.avg_audio_power ?? null
    const avgSilencePower =
      silencePowerStat?.mean ?? resultData?.avg_silence_power ?? null
    const validAudioCount =
      audioPowerStat?.number_of_data_points ??
      resultData?.valid_audio_count ??
      0
    const validSilenceCount =
      silencePowerStat?.number_of_data_points ??
      resultData?.valid_silence_count ??
      0

    // Determine quality assessment based on SNR value
    if (value >= 20) {
      qualityAssessment = "Excellent"
      qualityColor = "success"
    } else if (value >= 15) {
      qualityAssessment = "Good"
      qualityColor = "primary"
    } else if (value >= 10) {
      qualityAssessment = "Fair"
      qualityColor = "warning"
    } else if (value >= 0) {
      qualityAssessment = "Poor"
      qualityColor = "danger"
    } else {
      qualityAssessment = "Invalid"
      qualityColor = "danger"
    }

    return (
      <div className="space-y-4">
        <Spacer y={2} />
        <div>
          <div className="flex items-center gap-2">
            <span className="text-3xl font-bold">
              {value === -1 ? "N/A" : `${value.toFixed(1)} ${this.unit}`}
            </span>
            <Chip color={qualityColor} variant="flat" size="sm">
              {qualityAssessment}
            </Chip>
          </div>
        </div>

        {avgAudioPower !== null && avgSilencePower !== null && (
          <div>
            <h2 className="font-semibold">Power Analysis</h2>
            <div className="space-y-1 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="text-sm text-amber-600 font-medium mb-1">
                    Audio Signal Power
                  </div>
                  <div className="text-2xl font-bold text-amber-800">
                    {avgAudioPower.toFixed(1)} {this.unit}
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="text-sm text-amber-600 font-medium mb-1">
                    Background Noise Power
                  </div>
                  <div className="text-2xl font-bold text-amber-800">
                    {avgSilencePower.toFixed(1)} {this.unit}
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="text-sm text-amber-600 font-medium mb-1">
                    SNR (Difference)
                  </div>
                  <div className="text-2xl font-bold text-amber-800">
                    {value.toFixed(1)} {this.unit}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {validAudioCount !== null && validSilenceCount !== null && (
          <div>
            <h2 className="font-semibold">Segment Analysis</h2>
            <div className="space-y-1 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="text-sm text-amber-600 font-medium mb-1">
                    Valid Audio Segments
                  </div>
                  <div className="text-2xl font-bold text-amber-800">
                    {validAudioCount}
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="text-sm text-amber-600 font-medium mb-1">
                    Valid Silence Segments
                  </div>
                  <div className="text-2xl font-bold text-amber-800">
                    {validSilenceCount}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div>
          <h3 className="font-semibold">Quality Interpretation</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-coval-success-text" />
              <span>
                <strong>≥20 dB:</strong> Excellent - Clear audio with minimal
                background noise
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-coval-info-text" />
              <span>
                <strong>15-20 dB:</strong> Good - Acceptable audio quality for
                most applications
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-coval-warning-text" />
              <span>
                <strong>10-15 dB:</strong> Fair - Some background noise may be
                noticeable
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-coval-danger-text" />
              <span>
                <strong>0-10 dB:</strong> Poor - Significant background noise
                affecting clarity
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-coval-danger-text" />
              <span>
                <strong>-1:</strong> Invalid - Insufficient audio/silence
                segments for calculation
              </span>
            </div>
          </div>
        </div>
      </div>
    )
  }
}
